import axios, { AxiosError } from 'axios';
import { MapData, SearchResponse, CommunityComprehensiveAnalytics, ComparisonMode } from '../types/api';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // Increased to 30 seconds for data processing
});

// Token validation response type
export interface TokenValidationResponse {
  valid: boolean;
  corporation_id: string;
  accessible_corporations: string[];
  is_admin: boolean;
}

// Token validation error type
export interface TokenValidationError {
  status: number;
  message: string;
  isAccessDenied: boolean;
}

// Simple request queue with caching for comprehensive analytics
class RequestQueue {
  private queue: Array<() => Promise<any>> = [];
  private running = 0;
  private maxConcurrent = 6; // Limit to 6 concurrent requests
  private cache = new Map<string, any>(); // Simple cache
  private pendingRequests = new Map<string, Promise<any>>(); // Track pending requests

  async add<T>(key: string, requestFn: () => Promise<T>): Promise<T> {
    // Check if we already have this data cached
    if (this.cache.has(key)) {
      return this.cache.get(key);
    }

    // Check if this request is already pending
    if (this.pendingRequests.has(key)) {
      return this.pendingRequests.get(key);
    }

    // Create the promise for this request
    const requestPromise = new Promise<T>((resolve, reject) => {
      this.queue.push(async () => {
        try {
          this.running++;
          const result = await requestFn();
          
          // Cache the result
          this.cache.set(key, result);
          this.pendingRequests.delete(key);
          
          resolve(result);
        } catch (error) {
          // Remove from pending on error
          this.pendingRequests.delete(key);
          reject(error);
        } finally {
          this.running--;
          this.processNext();
        }
      });
      
      this.processNext();
    });

    // Track this as a pending request
    this.pendingRequests.set(key, requestPromise);
    
    return requestPromise;
  }

  private processNext() {
    if (this.running < this.maxConcurrent && this.queue.length > 0) {
      const next = this.queue.shift();
      if (next) {
        next();
      }
    }
  }

  // Optional: Clear cache if needed
  clearCache() {
    this.cache.clear();
  }

  // Optional: Remove specific cache entry
  clearCacheEntry(key: string) {
    this.cache.delete(key);
  }
}

// Create a single instance for comprehensive analytics requests
const comprehensiveAnalyticsQueue = new RequestQueue();

export const geoAPI = {
  // Get all locations with optional filtering
  getLocations: async (params?: {
    limit?: number;
    care_level?: string;
    corporation_id?: string;
    community_id?: string;
    min_lat?: number;
    max_lat?: number;
    min_lng?: number;
    max_lng?: number;
  }): Promise<MapData> => {
    const response = await api.get('/locations', { params });
    return response.data;
  },

  // Search for communities by name
  searchCommunities: async (query: string, limit: number = 10, filters?: { corporation_id?: string; community_id?: string }): Promise<SearchResponse> => {
    const params: any = { q: query, limit };
    if (filters?.corporation_id) params.corporation_id = filters.corporation_id;
    if (filters?.community_id) params.community_id = filters.community_id;
    const response = await api.get('/locations/search', {
      params
    });
    return response.data;
  },

  // Enhanced Analytics Endpoints
  getCommunityAnalytics: async (params?: {
    care_level?: string;
    corporation?: string;
    min_residents?: number;
    limit?: number;
  }) => {
    const response = await api.get('/analytics/communities', { params });
    return response.data;
  },

  getCorporationAnalytics: async () => {
    const response = await api.get('/analytics/corporations');
    return response.data;
  },

  getCorporation: async (corporationId: string) => {
    const response = await api.get(`/corporations/${corporationId}`);
    return response.data;
  },

  getCommunityCompetitors: async (communityName: string, radiusMiles: number = 80) => {
    const response = await api.get(`/analytics/competitors/${encodeURIComponent(communityName)}`, {
      params: { radius_miles: radiusMiles }
    });
    return response.data;
  },

  getAgeBandAnalytics: async () => {
    const response = await api.get('/analytics/age-bands');
    return response.data;
  },

  // Get comprehensive analytics for a specific community (with request gating)
  getCommunityComprehensiveAnalytics: async (communityId: string, radiusMiles: number = 80, careLevel?: string, comparisonMode: ComparisonMode = 'radius'): Promise<CommunityComprehensiveAnalytics> => {
    const cacheKey = `${communityId}-${radiusMiles}-${careLevel || 'null'}-${comparisonMode}`;
    return comprehensiveAnalyticsQueue.add(cacheKey, async () => {
      const params: any = { radius_miles: radiusMiles, comparison_mode: comparisonMode };
      if (careLevel) {
        params.care_level = careLevel;
      }
      const response = await api.get(`/analytics/community/${communityId}/comprehensive`, { params });
      return response.data;
    });
  },
  // Health check
  healthCheck: async (): Promise<{
    status: string;
    data_loaded: boolean;
    record_count: number;
  }> => {
    const response = await api.get('/health');
    return response.data;
  },

  // Validate user token and corporation/community access
  validateAccess: async (params: { corporationId?: string; communityId?: string; userToken: string }): Promise<TokenValidationResponse> => {
    try {
      const queryParams: any = {
        user_token: params.userToken
      };
      if (params.corporationId) queryParams.corporation_id = params.corporationId;
      if (params.communityId) queryParams.community_id = params.communityId;

      const response = await api.get('/auth/validate', {
        params: queryParams
      });
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<{ detail: string }>;
      const status = axiosError.response?.status || 500;
      const message = axiosError.response?.data?.detail || 'Token validation failed';
      
      const validationError: TokenValidationError = {
        status,
        message,
        isAccessDenied: status === 403
      };
      
      throw validationError;
    }
  },

  // Validate user token only (without corporation check)
  validateToken: async (userToken: string): Promise<{ valid: boolean; is_admin: boolean }> => {
    try {
      const response = await api.get('/auth/validate-token', {
        params: {
          user_token: userToken
        }
      });
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<{ detail: string }>;
      const status = axiosError.response?.status || 500;
      const message = axiosError.response?.data?.detail || 'Token validation failed';
      
      const validationError: TokenValidationError = {
        status,
        message,
        isAccessDenied: status === 403
      };
      
      throw validationError;
    }
  },
};

export default api;
